import { IconNames, IconProps } from '@dixa/lib-shared-habla';
import { ReactNode } from 'react';

import { CommandName } from 'utils/commands';
import { AppPath } from 'utils/url/appPaths';

/** The available command launcher scopes. */
export enum CommandLauncherScope {
  Main = '',
  User = '@',
  Conversation = '#',
  Macro = '!',
}

/** Represents the action in command launcher. */
export type CommandLauncherAction = {
  /** The id of the action. */
  readonly id: string;
  /** The label of the action. */
  readonly label: string;
  /** Alternative search terms for the action. */
  readonly aliases?: readonly string[];
  /** The icon to display before the label. */
  readonly icon?: IconNames;
  /** The color of the icon (e.g., 'purple.500', 'text.primary'). */
  readonly iconColor?: IconProps['color'];
  /** The content to be added before the label */
  readonly prefix?: ReactNode;
  /** The content to be added after the label */
  readonly suffix?: ReactNode;
  /** The routes that the action should be scoped to. */
  readonly routes?: readonly AppPath[];
  /** The name of the section the action belongs to. */
  readonly section?: string;
  /** The hotkey of the action. */
  readonly hotkey?: readonly string[];
  /** The scope of where the action should be visible. */
  readonly scope?: readonly CommandLauncherScope[];
  /** The nested actions. */
  readonly actions?: readonly CommandLauncherAction[];
  /** The perform function of the action. */
  readonly performFn?: () => void;
  /** Whether to keep the modal open after performing the action. */
  readonly keepOpen?: boolean;
  /** The command name to link to for keyboard shortcut display. */
  readonly commandName?: CommandName;
};

/** Represents the function for registering actions. */
export type CommandLauncherRegisterActionsFn = (
  actions: readonly CommandLauncherAction[],
) => () => void;

/** Represents the function for performing an action. */
export type CommandLauncherPerformActionFn = (
  action: CommandLauncherAction,
) => void;

/** Represents the function for creating an action. */
export type CommandLauncherCreateActionFn = (
  action: Omit<CommandLauncherAction, 'id'>,
) => CommandLauncherAction;
